import { HttpClient, HttpDownloadProgressEvent, HttpEventType } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Subscription } from 'rxjs';
import { environment } from '../../../environments/environment';
import { TabType } from '../../enums/TabType';
import { AgentAttachment, AgentChoice, AgentDiagnostic, AgentWorkspaceContext } from '../interfaces/agent-session';
import { AgentSessionStore } from './agent-session.store';
import { AgentWorkspaceRegistry } from './agent-workspace-registry.service';
import { LiveCreatorAdapter, LiveConflict, LiveRequest } from './live-creator-adapter.service';
import { WorkspaceViewSaveService } from '../../services/workspace-view-save.service';
import { AgentAppActions, parseAppActions } from './agent-app-actions.service';
import { AgentMutationJournal } from '../../interfaces/live-creator.contract';
import { AgentStreamParser, isRecord } from './agent-stream';
import { simulationConfigurationFingerprint } from '../simulation-result-digest';
import { AppAction, SimulationWorkflowContinuation, SimulationWorkflowStep } from '../interfaces/app-action';

interface ActiveRequest {
  id: string;
  scope: AgentWorkspaceContext;
  readonly messageId: string;
  readonly subscription: Subscription;
  readonly sessionId: string;
  readonly originalPrompt: string;
  readonly attachments: readonly AgentAttachment[];
  finished: boolean;
  sequence: number;
  live?: LiveRequest;
  finalizing?: boolean;
  rebaseCount: number;
  allowSave: boolean;
  application: ReturnType<AgentWorkspaceRegistry['applicationMetadata']>;
  transportDone?: Promise<void>;
  transportComplete?: () => void;
  prior?: AgentMutationJournal;
  choice?: { choiceId: string; optionId: string };
  postApplyInterpret?: boolean;
  /** Browser wall-clock benchmark spanning every Runtime continuation and frontend apply step. */
  benchmarkStartedAtMs: number;
  dispatchCount: number;
  simulationWorkflow?: { remaining: SimulationWorkflowStep[]; persistenceIntent: SimulationWorkflowContinuation['persistenceIntent'];
    revision: number; fingerprint: string; modelUuid: string; tabInstanceId: string; dataServiceId: string;
    completed: string[]; interpreted?: string; awaitingInterpret?: boolean; ran?: boolean };
}

@Injectable({ providedIn: 'root' })
export class AgentClientService {
  static readonly maxAttachments = 5;
  static readonly maxAttachmentBytes = 10 * 1024 * 1024;
  static readonly maxAttachmentTotalBytes = 20 * 1024 * 1024;
  /** Keep the most recent ten Runtime traces (each trace is capped at 96 events upstream). */
  static readonly maxDiagnosticEvents = 960;
  private readonly http = inject(HttpClient);
  private readonly sessions = inject(AgentSessionStore);
  private readonly workspaces = inject(AgentWorkspaceRegistry);
  private readonly liveAdapter = inject(LiveCreatorAdapter);
  private readonly appActions = inject(AgentAppActions);
  private readonly viewSave = inject(WorkspaceViewSaveService);
  private readonly createdRecovery = new Map<string, { scope: AgentWorkspaceContext; model: unknown; originalPrompt: string }>();
  private readonly recovery = new Map<string, { scope: AgentWorkspaceContext; journal: AgentMutationJournal; originalId: string }>();
  private readonly active = new Map<string, ActiveRequest>();

  canSend(context: AgentWorkspaceContext): boolean {
    if (context.loading || (context.kind === 'tab' && !context.dataServiceId) || (context.modelUuid && !context.hierarchy)) return false;
    return true;
  }

  async addAttachments(scopeId: string, files: readonly File[]): Promise<void> {
    const session = this.sessions.ensure(scopeId);
    const available = AgentClientService.maxAttachments - session.attachments.length;
    if (!files.length || available <= 0) {
      this.sessions.update(scopeId, current => ({ ...current, attachmentError: `You can attach up to ${AgentClientService.maxAttachments} files.` }));
      return;
    }
    const selected = files.slice(0, available);
    try {
      const attachments = await Promise.all(selected.map(file => this.readAttachment(file)));
      const latest = this.sessions.ensure(scopeId);
      const total = latest.attachments.reduce((sum, item) => sum + item.sizeBytes, 0)
        + attachments.reduce((sum, item) => sum + item.sizeBytes, 0);
      if (total > AgentClientService.maxAttachmentTotalBytes) throw new Error('Attached files must total 20 MB or less.');
      this.sessions.update(scopeId, current => ({ ...current, attachments: [...current.attachments, ...attachments], attachmentError: undefined }));
    } catch (error) {
      const message = error instanceof Error ? error.message : 'The selected file could not be attached.';
      this.sessions.update(scopeId, current => ({ ...current, attachmentError: message }));
    }
  }

  removeAttachment(scopeId: string, index: number): void {
    this.sessions.update(scopeId, current => ({ ...current, attachments: current.attachments.filter((_, itemIndex) => itemIndex !== index), attachmentError: undefined }));
  }

  send(scopeId: string): void {
    // A prior Structured Choice may finish a multi-step request (Model then
    // parent, for example), but must not silently become the target of a new
    // free-form request. The next turn will therefore ask again when several
    // Models are represented.
    this.workspaces.clearRepresentedModel(scopeId);
    const scope = this.workspaces.capture(scopeId);
    const session = this.sessions.ensure(scopeId);
    const prompt = session.draft.trim();
    if (!scope || !prompt || this.active.has(scopeId) || !this.canSend(scope)) return;
    const request: ActiveRequest = { id: crypto.randomUUID(), scope,
      messageId: crypto.randomUUID(), subscription: new Subscription(), sessionId: session.id, originalPrompt: prompt, attachments: session.attachments,
      finished: false, sequence: 0, rebaseCount: 0, allowSave: true, application: this.workspaces.applicationMetadata(),
      benchmarkStartedAtMs: performance.now(), dispatchCount: 0 };
    this.active.set(scopeId, request);
    this.sessions.update(scopeId, previous => ({ ...previous, draft: '', attachments: [], attachmentError: undefined, status: 'streaming', error: undefined,
      // A typed choice belongs to the request that issued it. Sending free
      // text is an intentional alternative path, so stale buttons must not
      // remain actionable or visually persist after that reply.
      choices: previous.choices.map(choice => choice.status === 'pending' || choice.status === 'submitting'
        ? { ...choice, status: 'expired' } : choice),
      activities: [], messages: [...previous.messages,
        { id: crypto.randomUUID(), role: 'user', text: prompt, ...(request.attachments.length ? { attachmentNames: request.attachments.map(item => item.name) } : {}) },
        { id: request.messageId, role: 'assistant', text: '' }] }));
    const created = this.createdRecovery.get(scopeId);
    if (created) {
      void this.liveAdapter.loadCreatedModel(created.scope, created.model, () => !request.finished)
        .then(() => {
          this.createdRecovery.delete(scopeId);
          return this.continueCreatedModel(request, created.originalPrompt, this.createdLayoutProfile(created.model));
        }).catch(() => this.fail(request, 'The Model was already created, but could not be loaded in its original Creator. No duplicate was created.'));
      return;
    }
    // A saved View owns a live DataViewer Canvas too. Once its Model identity is
    // resolved, its edits must use the same live protocol as a Creator rather
    // than the detached persisted-View tools.
    if (this.requiresLiveCanvas(scope, prompt)) {
      const pending = this.recovery.get(scopeId);
      const recovery = pending && this.workspaces.isCurrent(pending.scope) ? pending : undefined;
      if (pending && !recovery) this.recovery.delete(scopeId);
      request.prior = recovery?.journal;
      void this.liveAdapter.capture(scope, request.id, recovery?.journal,
        recovery ? { originalRequestId: recovery.originalId, reason: 'CANVAS_APPLY_FAILED' } : undefined).then(live => {
        if (request.finished) { this.liveAdapter.release(live); return; }
        request.live = live;
        this.dispatch(request, session.id, prompt);
      }).catch(() => this.fail(request, 'The Canvas could not be captured. Finish any edit and confirm the selected Model.'));
    } else this.dispatch(request, session.id, prompt);
  }

  submitChoice(scopeId: string, choice: AgentChoice, optionId: string): void {
    const scope = this.workspaces.capture(scopeId);
    const session = this.sessions.ensure(scopeId);
    const option = choice.options.find(item => item.optionId === optionId);
    if (!scope || !option || this.active.has(scopeId) || choice.status !== 'pending'
        || choice.scopeId !== scope.scopeId || choice.requestId === ''
        || (scope.kind === 'tab' && (choice.tabInstanceId !== scope.tabInstanceId
          || choice.contextRevision !== scope.contextRevision))) {
      this.sessions.update(scopeId, current => ({ ...current, choices: current.choices.map(item =>
        item.choiceId === choice.choiceId ? { ...item, status: 'expired' } : item) }));
      return;
    }
    this.workspaces.selectRepresentedModel(scopeId, optionId);
    const selectedScope = this.workspaces.capture(scopeId);
    if (!selectedScope) return;
    const request: ActiveRequest = { id: choice.requestId, scope: selectedScope, messageId: crypto.randomUUID(),
      subscription: new Subscription(), sessionId: session.id, originalPrompt: '', attachments: [], finished: false,
      sequence: 0, rebaseCount: 0, allowSave: true, application: this.workspaces.applicationMetadata(),
      benchmarkStartedAtMs: performance.now(), dispatchCount: 0,
      choice: { choiceId: choice.choiceId, optionId } };
    this.active.set(scopeId, request);
    this.sessions.update(scopeId, current => ({ ...current, status: 'streaming', error: undefined, activities: [],
      choices: current.choices.map(item => item.choiceId === choice.choiceId
        ? { ...item, status: 'submitting', selectedOptionId: optionId } : item),
      messages: [...current.messages, { id: crypto.randomUUID(), role: 'user', text: option.label },
        { id: request.messageId, role: 'assistant', text: '' }] }));
    if (choice.requiresLiveCreator && this.usesLiveCreator(selectedScope)) {
      void this.liveAdapter.capture(selectedScope, request.id).then(live => {
        if (request.finished) { this.liveAdapter.release(live); return; }
        request.live = live;
        this.dispatch(request, session.id, '');
      }).catch(() => this.fail(request, 'The Canvas could not be captured. Review the workspace before choosing again.'));
    } else this.dispatch(request, session.id, '');
  }

  private dispatch(request: ActiveRequest, sessionId: string, prompt: string): void {
    request.dispatchCount++;
    const scope = request.scope;
    request.transportDone = new Promise(resolve => { request.transportComplete = resolve; });
    const parser = new AgentStreamParser();
    let received = 0;
    const consume = (text: string) => {
      if (text.length < received) throw new Error('Invalid agent stream.');
      const events = parser.push(text.slice(received));
      received = text.length;
      for (const event of events) this.accept(request, event);
    };
    const wireScope = scope.kind === 'application'
      ? { kind: 'application', scope_id: 'application', app_revision: scope.contextRevision }
      : { kind: 'tab', scope_id: scope.scopeId, tab_instance_id: scope.tabInstanceId, tab_type: scope.tabType,
          data_service_id: scope.dataServiceId, context_revision: scope.contextRevision,
          model_target_state: scope.modelTargetState ?? null, model_uuid: scope.modelUuid ?? null, hierarchy: scope.hierarchy ?? null,
          view_uuid: scope.viewUuid, view_name: scope.viewName ?? null,
          represented_models: (scope.representedModels ?? []).map(model => ({ model_uuid: model.modelUuid,
            hierarchy: model.hierarchy, ...(model.name ? { name: model.name } : {}) })),
          ...(scope.simulation ? { simulation: { active: true, revision: scope.simulation.revision, dirty: scope.simulation.dirty,
            saved_simulation_uuid: scope.simulation.selectedSimulationId, model_uuid: scope.simulation.modelUuid,
            saved_simulation_name: scope.simulation.selectedSimulationName ?? null,
            configuration: scope.simulation.configuration, has_last_result: scope.simulation.hasLastResult,
            result_source: scope.simulation.resultSource, result_freshness: scope.simulation.resultFreshness,
            executed_at: scope.simulation.executedAt, executed_simulation_revision: scope.simulation.executedSimulationRevision,
            ...(scope.simulation.result ? { result: scope.simulation.result } : {}) } } : {}) };
    const choiceResponse = request.choice ? { choice_id: request.choice.choiceId, option_id: request.choice.optionId,
      request_id: request.id, session_id: sessionId, scope_id: scope.scopeId,
      ...(scope.kind === 'tab' ? { tab_instance_id: scope.tabInstanceId, context_revision: scope.contextRevision } : {}) } : undefined;
    request.subscription.add(this.http.post(`${environment.AGENT_API_URL || environment.API_URL}/agent/chat`, {
      request_id: request.id, session_id: sessionId, ...(choiceResponse ? { choice_response: choiceResponse } : { prompt }),
      ...(!choiceResponse && request.attachments.length ? { attachments: request.attachments.map(item => ({
        name: item.name, mime_type: item.mimeType, size_bytes: item.sizeBytes, data_base64: item.dataBase64
      })) } : {}), scope: wireScope, application: request.application,
      ...(request.live ? { live_creator: request.live.context } : {})
    }, { observe: 'events', responseType: 'text', reportProgress: true }).subscribe({
      next: event => {
        try {
          if (event.type === HttpEventType.DownloadProgress) {
            const progress = event as HttpDownloadProgressEvent;
            if (progress.partialText !== undefined) consume(progress.partialText);
          }
          if (event.type === HttpEventType.Response) { consume(event.body ?? ''); parser.finish(); }
        } catch {
          this.fail(request, 'The agent response could not be processed. Review the workspace before retrying.');
        }
      },
      error: () => { request.transportComplete?.(); this.fail(request, 'The agent connection failed. Review the workspace before retrying any changes.'); },
      complete: () => {
        request.transportComplete?.();
        if (!request.finished && !request.finalizing) this.fail(request, 'The agent response was interrupted.');
      }
    }));
  }

  stop(scopeId: string): void {
    const request = this.active.get(scopeId);
    if (!request) return;
    this.completeBenchmark(request, 'cancelled');
    request.finished = true;
    request.simulationWorkflow = undefined;
    request.transportComplete?.();
    if (request.live) this.liveAdapter.release(request.live);
    this.active.delete(scopeId);
    request.subscription.unsubscribe();
    this.sessions.update(scopeId, session => ({ ...session, status: 'cancelled', error: undefined }));
  }

  reset(): void {
    for (const id of [...this.active.keys()]) this.stop(id);
    this.sessions.clear();
    this.recovery.clear();
    this.createdRecovery.clear();
  }

  private accept(request: ActiveRequest, event: unknown): void {
    if (request.finished || this.active.get(request.scope.scopeId) !== request) return;
    if (!isRecord(event)) throw new Error('Invalid agent event.');
    if (event['type'] === 'final') {
      const payload = event['payload'];
      if (!isRecord(payload) || !isRecord(payload['request']) || !isRecord(payload['response'])) throw new Error('Invalid final response.');
      const correlation = payload['request'];
      if (correlation['requestId'] !== request.id || correlation['scopeId'] !== request.scope.scopeId
          || (request.scope.kind === 'tab' && (correlation['targetTabId'] !== request.scope.tabInstanceId
              || correlation['expectedContextRevision'] !== request.scope.contextRevision))) throw new Error('Stale response.');
      const actions = parseAppActions(payload['appActions']);
      const compatibleRestore = actions.length > 0
        && actions.every(action => action.type === 'RESTORE_VIEW' && action.destination === 'CURRENT_CREATOR')
        && this.workspaces.isCompatibleRestoreTarget(request.scope);
      if (!this.workspaces.isCurrent(request.scope) && !compatibleRestore) {
        this.fail(request, 'This workspace changed or closed while the agent was working.');
        return;
      }
      if (request.finalizing) throw new Error('Duplicate final response');
      request.finalizing = true;
      void this.finish(request, payload).catch(error => {
        if (error instanceof Error && error.message.startsWith('Simulation workflow ')) {
          this.fail(request, error.message);
          return;
        }
        const reasons = new Set(['Invalid live commit', 'Invalid mutation journal', 'Invalid patch identity',
          'Unknown operation field', 'Invalid geometry', 'Missing operation field', 'Unknown shape',
          'Invalid batch ACK', 'Unexpected batch ACK', 'Invalid save request', 'Invalid identity/style',
          'Invalid Canvas operations', 'Unknown Canvas operation', 'Unknown color', 'Unknown arrow style',
          'Unknown arrow type', 'Unknown anchor']);
        const safeMessage = error instanceof Error && (
          reasons.has(error.message) || error.message.startsWith('Unknown operation field: ')
          || error.message.startsWith('Canvas operation failed')
        ) ? error.message : '';
        if (actionsIncludeViewRestore(payload['appActions'])) {
          const detail = error instanceof Error && error.message ? ` (${error.message})` : '';
          this.fail(request, `The saved View could not be restored${detail}.`);
          return;
        }
        if (payload['createdModel'] != null) {
          this.fail(request, 'The Model was created, but its live Canvas could not be prepared. See Agent diagnostics for the step that failed; retrying will not create a duplicate Model.');
          return;
        }
        const simulationAction = actions.find(action => action.type === 'APPLY_SIMULATION_CONFIG'
          || action.type === 'RUN_SIMULATION' || action.type === 'SAVE_SIMULATION_CONFIG');
        if (simulationAction) {
          const operation = simulationAction.type === 'APPLY_SIMULATION_CONFIG' ? 'Apply'
            : simulationAction.type === 'RUN_SIMULATION' ? 'Run' : 'Save';
          const detail = error instanceof Error && error.message ? `: ${error.message}` : '';
          this.fail(request, `Simulation ${operation} failed${detail}. No dependent action was started.`);
          return;
        }
        const modelLoad = actions.find(action => action.type === 'LOAD_MODEL');
        if (modelLoad?.type === 'LOAD_MODEL') {
          this.fail(request, `Could not load Model “${modelLoad.modelName}”: ${this.operationFailureMessage(error)}.`);
          return;
        }
        const reason = safeMessage ? ` (${safeMessage})` : '';
        this.fail(request, `The result could not be applied or saved${reason}. Review the Canvas before retrying.`);
      });
      return;
    }
    if (event['requestId'] !== request.id) throw new Error('Request identity mismatch.');
    if (event['type'] === 'message.delta') {
      if (typeof event['text'] !== 'string' || typeof event['sequence'] !== 'number' || event['sequence'] <= request.sequence) throw new Error('Invalid text delta.');
      request.sequence = event['sequence'];
      const text = event['text'];
      this.sessions.update(request.scope.scopeId, session => ({ ...session,
        messages: session.messages.map(message => message.id === request.messageId ? { ...message, text: message.text + text } : message) }));
    } else if (event['type'] === 'choice') {
      const choice = event['choice'];
      if (!isRecord(choice) || typeof choice['choiceId'] !== 'string' || choice['requestId'] !== request.id
          || choice['scopeId'] !== request.scope.scopeId || typeof choice['message'] !== 'string'
          || choice['choice_protocol_version'] !== 1
          || !Array.isArray(choice['options']) || choice['options'].length < 2 || choice['options'].length > 12
          || choice['options'].some(option => !isRecord(option) || typeof option['optionId'] !== 'string'
            || typeof option['label'] !== 'string')) throw new Error('Invalid structured choice.');
      if (request.scope.kind === 'tab' && (choice['tabInstanceId'] !== request.scope.tabInstanceId
          || choice['contextRevision'] !== request.scope.contextRevision)) throw new Error('Stale choice.');
      const typed: AgentChoice = { choiceId: choice['choiceId'], requestId: request.id, scopeId: request.scope.scopeId,
        ...(request.scope.kind === 'tab' ? { tabInstanceId: choice['tabInstanceId'] as string,
          contextRevision: choice['contextRevision'] as number } : {}), message: choice['message'],
        options: choice['options'].map(option => ({ optionId: option['optionId'] as string, label: option['label'] as string })),
        status: 'pending', requiresLiveCreator: this.usesLiveCreator(request.scope) };
      this.sessions.update(request.scope.scopeId, session => ({ ...session,
        choices: [...session.choices.filter(item => item.choiceId !== typed.choiceId), typed] }));
    } else if (event['type'] === 'diagnostic') {
      const trace = event['trace'];
      if (typeof event['requestId'] !== 'string' || event['requestId'] !== request.id || !isRecord(trace)
          || typeof trace['stage'] !== 'string' || typeof trace['status'] !== 'string'
          || (trace['tool'] !== undefined && typeof trace['tool'] !== 'string') || !isRecord(trace['details'])) {
        throw new Error('Invalid diagnostic.');
      }
      const diagnostic: AgentDiagnostic = { requestId: request.id, stage: trace['stage'], status: trace['status'],
        ...(typeof trace['tool'] === 'string' ? { tool: trace['tool'] } : {}), details: trace['details'] };
      this.sessions.update(request.scope.scopeId, session => ({ ...session,
        diagnostics: [...session.diagnostics, diagnostic].slice(-AgentClientService.maxDiagnosticEvents) }));
    } else if (event['type'] === 'activity.started' || event['type'] === 'activity.completed') {
      if (typeof event['activityId'] !== 'string' || typeof event['label'] !== 'string') throw new Error('Invalid activity.');
      const activity = { id: event['activityId'], label: event['label'], completed: event['type'] === 'activity.completed' };
      this.sessions.update(request.scope.scopeId, session => ({ ...session,
        // Progress is a compact status line, not a tool log. Full history is
        // available below in Agent diagnostics; retain only the latest event.
        activities: [activity] }));
    } else if (event['type'] === 'error') {
      this.fail(request, 'The agent could not complete the request. Review the workspace before retrying changes.');
    } else throw new Error('Unknown agent event.');
  }

  private async finish(request: ActiveRequest, payload: Record<string, unknown>): Promise<void> {
    const actions = parseAppActions(payload['appActions']);
    const rawCommit = payload['liveCreatorCommit'];
    if (actions.length && isRecord(rawCommit) && (rawCommit['canvas_patch'] || rawCommit['save_request'])) throw new Error('Navigation cannot be combined with Canvas changes');
    if (!request.allowSave && isRecord(rawCommit) && rawCommit['save_request'] != null) throw new Error('Unexpected save request');
    const response = payload['response'];
    if (!isRecord(response) || typeof response['text'] !== 'string') throw new Error('Invalid response text');
    if (request.simulationWorkflow?.awaitingInterpret) {
      if (actions.length || payload['createdModel'] != null || payload['liveCreatorCommit'] != null) {
        throw new Error('Simulation workflow interpretation attempted a mutation');
      }
      request.simulationWorkflow.awaitingInterpret = false;
      request.simulationWorkflow.interpreted = response['text'];
      await this.advanceSimulationWorkflow(request);
      return;
    }
    const stillActive = () => !request.finished && this.active.get(request.scope.scopeId) === request;
    if (payload['createdModel'] != null) {
      if (request.live || actions.length) throw new Error('Created Model cannot be combined with other actions');
      this.createdRecovery.set(request.scope.scopeId, {
        scope: request.scope, model: payload['createdModel'], originalPrompt: request.originalPrompt
      });
      const loadStartedAt = this.startFrontendOperation(request, 'load_created_model');
      try {
        await this.liveAdapter.loadCreatedModel(request.scope, payload['createdModel'], stillActive);
      } catch (error) {
        this.finishFrontendOperation(request, 'load_created_model', loadStartedAt, 'failed',
          { reason: this.createdModelFailureReason(error) });
        throw error;
      }
      this.finishFrontendOperation(request, 'load_created_model', loadStartedAt);
      this.createdRecovery.delete(request.scope.scopeId);
      const preparedLayout = this.createdInitialLayout(payload['createdModel']);
      if (preparedLayout) {
        const nextScope = this.workspaces.capture(request.scope.scopeId);
        if (!nextScope || nextScope.tabType !== TabType.MODEL_CREATOR
            || !nextScope.modelUuid || !nextScope.hierarchy) {
          throw new Error('Created Model Canvas unavailable');
        }
        request.id = crypto.randomUUID();
        request.scope = nextScope;
        const layoutStartedAt = this.startFrontendOperation(request, 'apply_created_model_initial_layout');
        try {
          await this.liveAdapter.applyCreatedModelInitialLayout(
            nextScope, request.id, preparedLayout, stillActive
          );
          this.finishFrontendOperation(request, 'apply_created_model_initial_layout', layoutStartedAt);
        } catch (error) {
          this.finishFrontendOperation(request, 'apply_created_model_initial_layout', layoutStartedAt, 'failed',
            { reason: this.createdModelFailureReason(error) });
          throw error;
        }
        if (!stillActive()) return;
        this.sessions.update(request.scope.scopeId, session => ({ ...session, status: 'idle',
          choices: this.resolveChoice(session.choices, request),
          messages: session.messages.map(message => message.id === request.messageId
            ? { ...message, text: response['text'] as string } : message) }));
        this.completeBenchmark(request, 'completed');
        request.finished = true;
        this.recovery.delete(request.scope.scopeId);
        this.active.delete(request.scope.scopeId);
        return;
      }
      await this.continueCreatedModel(request, request.originalPrompt, this.createdLayoutProfile(payload['createdModel']));
      return;
    }
    // Navigation is terminal and must not be coupled to a live Canvas commit.
    // In particular, restoring a View changes the workspace by design, so a
    // stale Canvas acknowledgement must never prevent the requested restore.
    if (actions.length) {
      if (request.scope.tabType === TabType.SIMULATION && actions.some(action => action.type === 'SET_TAB_MODE' && action.continueSimulation)) {
        throw new Error('Simulation is already active; generate in the current workspace.');
      }
      if (request.live) this.liveAdapter.release(request.live);
      const actionStartedAt = this.startFrontendOperation(request, 'execute_application_actions', { action_count: actions.length });
      try {
        await this.appActions.executeBatch(actions, request.scope, request.application.app_revision);
        this.finishFrontendOperation(request, 'execute_application_actions', actionStartedAt, 'completed', { action_count: actions.length });
      } catch (error) {
        this.finishFrontendOperation(request, 'execute_application_actions', actionStartedAt, 'failed', { action_count: actions.length });
        throw error;
      }
      if (!stillActive()) return;
      if (actions.length === 1 && 'simulationWorkflow' in actions[0] && actions[0].simulationWorkflow) {
        await this.beginSimulationWorkflow(request, actions[0], response['text']);
        return;
      }
      if (actions.length === 1 && actions[0].type === 'APPLY_SIMULATION_CONFIG' && actions[0].afterApply) {
        const applied = this.workspaces.capture(request.scope.scopeId);
        await this.continueAfterSimulationApply(request, actions[0], applied);
        return;
      }
      if (actions.length === 1 && actions[0].type === 'SET_TAB_MODE' && actions[0].continueSimulation) {
        await this.continueInSimulation(request);
        return;
      }
      if (actions.length === 1 && actions[0].type === 'RUN_SIMULATION') {
        await this.continueAfterSimulationRun(request);
        return;
      }
      this.sessions.update(request.scope.scopeId, session => ({ ...session, status: 'idle', choices: this.resolveChoice(session.choices, request),
        messages: session.messages.map(message => message.id === request.messageId ? { ...message, text: response['text'] as string } : message) }));
      this.completeBenchmark(request, 'completed');
      request.finished = true;
      this.recovery.delete(request.scope.scopeId);
      this.active.delete(request.scope.scopeId);
      return;
    }
    if (request.live) {
      const applyStartedAt = this.startFrontendOperation(request, 'apply_live_canvas');
      try {
        const commit = await this.liveAdapter.apply(request.scope, request.live, payload['liveCreatorCommit'], stillActive);
        request.prior = this.mergeJournals(request.prior, commit.agent_mutation_journal);
        if (commit.save_request) {
          if (!stillActive()) return;
          await this.viewSave.save(request.live.owner, { name: commit.save_request.view_name,
            isCurrent: () => stillActive() && this.workspaces.isCurrent(request.scope) });
        }
        if (!stillActive()) return;
        this.liveAdapter.acknowledge(request.live, commit);
        this.finishFrontendOperation(request, 'apply_live_canvas', applyStartedAt);
      } catch (error) {
        this.finishFrontendOperation(request, 'apply_live_canvas', applyStartedAt, 'failed');
        if (error instanceof LiveConflict) request.prior = this.mergeJournals(request.prior, error.commit.agent_mutation_journal);
        // Only an actual Canvas revision race can be rebased. A rejected
        // semantic patch will not become valid by replaying the same request;
        // in particular, never re-run its completed canonical mutation.
        if (error instanceof LiveConflict && error.reason === 'CANVAS_APPLY_FAILED') {
          throw new Error(error.safeDetail ? `Canvas operation failed: ${error.safeDetail}` : 'Canvas operation failed');
        }
        if (error instanceof LiveConflict && error.reason === 'CANVAS_REVISION_CONFLICT'
            && stillActive() && request.rebaseCount < 2) {
          await request.transportDone;
          if (!stillActive()) return;
          this.liveAdapter.release(request.live);
          const originalId = request.live.context.rebase?.originalRequestId ?? request.id;
          request.id = crypto.randomUUID();
          request.rebaseCount++;
          request.sequence = 0;
          request.live = await this.liveAdapter.capture(request.scope, request.id, request.prior,
            { originalRequestId: originalId, reason: error.reason });
          if (!stillActive()) { this.liveAdapter.release(request.live); return; }
          request.finalizing = false;
          this.sessions.update(request.scope.scopeId, session => ({ ...session, activities: [],
            messages: session.messages.map(message => message.id === request.messageId ? { ...message, text: '' } : message) }));
          this.dispatch(request, this.sessions.ensure(request.scope.scopeId).id,
            `Continue fulfilling the original user request after an internal Canvas refresh.\n\n` +
            `Original user request:\n${request.originalPrompt}\n\n` +
            `Use the latest live Canvas snapshot and preserve all completed canonical Model mutations in prior_agent_mutation_journal; do not repeat them. ` +
            `The refresh is internal implementation detail: do not mention rebasing, Canvas revisions, snapshots, event batches, or recovery in the user-facing response. ` +
            `After the accepted Canvas patch, give a concise normal summary of the visual result for the original request.`);
          return;
        }
        throw error;
      }
    } else if (payload['liveCreatorCommit'] != null) throw new Error('Unexpected Canvas commit');
    if (!stillActive()) return;
    if (!stillActive()) return;
    this.sessions.update(request.scope.scopeId, session => ({ ...session, status: 'idle', choices: this.resolveChoice(session.choices, request),
      messages: session.messages.map(message => message.id === request.messageId ? { ...message, text: response['text'] as string } : message) }));
    this.completeBenchmark(request, 'completed');
    request.finished = true;
    this.recovery.delete(request.scope.scopeId);
    this.active.delete(request.scope.scopeId);
  }

  private async continueInSimulation(request: ActiveRequest): Promise<void> {
    await request.transportDone;
    const original = request.scope;
    for (let attempt = 0; attempt < 200; attempt++) {
      if (request.finished || this.active.get(original.scopeId) !== request) return;
      const next = this.workspaces.capture(original.scopeId);
      // Angular replaces the old DataViewer before the Simulation component is
      // available. During that small interval the registry deliberately has a
      // loading source without its connection yet. It is a normal transition,
      // not a user navigation or a loss of the original workspace.
      if (this.workspaces.activeId() !== original.scopeId) {
        throw new Error('The original Simulation workspace is no longer active.');
      }
      if (!next || next.loading || !next.dataServiceId) {
        await new Promise<void>(resolve => setTimeout(resolve, 50));
        continue;
      }
      if (next.dataServiceId !== original.dataServiceId) {
        throw new Error('The Simulation workspace changed Data Service before it was ready.');
      }
      if (next.tabType === TabType.SIMULATION && next.simulation?.modelUuid) {
        if (original.modelUuid && next.simulation.modelUuid !== original.modelUuid) {
          throw new Error('The selected Model changed before the Simulation was ready.');
        }
        request.id = crypto.randomUUID();
        request.scope = next;
        request.application = this.workspaces.applicationMetadata();
        request.sequence = 0;
        request.live = undefined;
        this.sessions.update(original.scopeId, session => ({ ...session, choices: this.resolveChoice(session.choices, request) }));
        request.choice = undefined;
        request.finalizing = false;
        request.allowSave = false;
        this.dispatch(request, request.sessionId,
          `The original tab is now mounted in Simulation mode. Continue the pending request using its current live Simulation configuration and revision. ` +
          `Complete the original Simulation operation now; it may be create, load, edit, save, run, or interpret. ` +
          `For generation or edits, validate and Apply before any dependent Run or Save. For a saved-configuration load, use the native load workflow and do not generate or Apply a replacement. ` +
          `Normalize any requested follow-up Save, Run, and result interpretation into the bounded simulation_workflow in the user-requested order. ` +
          `PersistenceIntent must be NONE unless the original CURRENT user request explicitly asks to save; never inherit Save from conversation history. ` +
          `For a Run-only continuation, post_apply_intent remains supported. ` +
          `Preserve the operational details and decisions from this conversation.\n` +
          `Original user request: ${request.originalPrompt}`);
        return;
      }
      await new Promise<void>(resolve => setTimeout(resolve, 50));
    }
    throw new Error('The Simulation workspace did not finish loading. Please try again.');
  }

  private async continueCreatedModel(request: ActiveRequest, originalPrompt: string,
      layoutProfile?: 'generic' | 'process_table'): Promise<void> {
    await request.transportDone;
    if (request.finished || this.active.get(request.scope.scopeId) !== request) return;
    const scopeId = request.scope.scopeId;
    const nextScope = this.workspaces.capture(scopeId);
    if (!nextScope || nextScope.tabType !== TabType.MODEL_CREATOR || !nextScope.modelUuid || !nextScope.hierarchy) {
      this.recordDiagnostic(scopeId, request.id, 'frontend', 'failed', {
        operation: 'start_created_model_canvas_design', reason: 'created_model_canvas_unavailable'
      });
      throw new Error('Created Model Canvas unavailable');
    }
    request.id = crypto.randomUUID();
    request.scope = nextScope;
    request.sequence = 0;
    request.rebaseCount = 0;
    request.finalizing = false;
    request.allowSave = false;
    request.application = this.workspaces.applicationMetadata();
    const captureStartedAt = this.startFrontendOperation(request, 'capture_created_model_canvas');
    try {
      request.live = await this.liveAdapter.capture(nextScope, request.id, undefined, undefined, true);
    } catch (error) {
      this.finishFrontendOperation(request, 'capture_created_model_canvas', captureStartedAt, 'failed',
        { reason: this.createdModelFailureReason(error) });
      throw error;
    }
    this.finishFrontendOperation(request, 'capture_created_model_canvas', captureStartedAt);
    if (request.finished || this.active.get(scopeId) !== request) {
      this.liveAdapter.release(request.live);
      return;
    }
    this.sessions.update(scopeId, session => ({ ...session, status: 'streaming', error: undefined,
      activities: [{ id: 'created-model-loaded', label: 'Model loaded in the live Creator', completed: true }],
      messages: session.messages.map(message => message.id === request.messageId ? { ...message, text: '' } : message) }));
    this.dispatch(request, request.sessionId,
      `Complete the automatic unsaved Canvas design for the Model just loaded.\n` +
      `Original request:\n${originalPrompt}\n\n` +
      (layoutProfile === 'process_table'
        ? `The creation phase selected layout_profile=process_table. Call prepare_created_process_table_layout exactly once; do not load a skill or author Canvas operations. `
        : layoutProfile === 'generic'
          ? `The creation phase selected layout_profile=generic; load only edt-layout-generic. Call get_created_model_layout_context once, design the complete visual operation set, validate it, then stage using only validation_id. `
          : `Classify the layout and load exactly one of edt-layout-generic or edt-layout-process-table. Call get_created_model_layout_context once, design the complete visual operation set, validate it, then stage using only validation_id. `) +
      `Preserve canonical semantics and never save or persist a View.`);
  }

  private createdLayoutProfile(raw: unknown): 'generic' | 'process_table' | undefined {
    if (!isRecord(raw)) return undefined;
    return raw['layout_profile'] === 'generic' || raw['layout_profile'] === 'process_table'
      ? raw['layout_profile'] : undefined;
  }

  private createdInitialLayout(raw: unknown): Record<string, unknown> | undefined {
    return isRecord(raw) && isRecord(raw['initial_layout']) ? raw['initial_layout'] : undefined;
  }

  /** A Run is asynchronous UI work. Resume with a fresh post-run snapshot so
   * the runtime can read the actual result without fabricating a user turn. */
  private async continueAfterSimulationRun(request: ActiveRequest): Promise<void> {
    await request.transportDone;
    if (request.finished || this.active.get(request.scope.scopeId) !== request) return;
    const next = this.workspaces.capture(request.scope.scopeId);
    if (!next || next.tabType !== TabType.SIMULATION || !next.simulation?.hasLastResult) {
      throw new Error('Simulation completed without an available result');
    }
    request.id = crypto.randomUUID();
    request.scope = next;
    request.application = this.workspaces.applicationMetadata();
    request.sequence = 0;
    request.finalizing = false;
    request.allowSave = false;
    this.sessions.update(next.scopeId, session => ({ ...session, status: 'streaming', activities: [],
      messages: session.messages.map(message => message.id === request.messageId ? { ...message, text: '' } : message) }));
    this.dispatch(request, request.sessionId,
      request.postApplyInterpret === true
      ? `The requested Simulation run has completed successfully. This is an internal action-completion continuation, not a new user request. ` +
        `Use the fresh Simulation snapshot. Call get_simulation_results and interpret only the measured result. ` +
        `Do not run, save, or modify the Simulation again.`
      : request.postApplyInterpret === false
      ? `The requested Simulation run has completed successfully. This is an internal action-completion continuation, not a new user request. ` +
        `Report the completed run without interpreting results. Do not run, save, or modify the Simulation again.`
      :
      `The requested Simulation run has completed successfully. This is an internal action-completion continuation, not a new user request. ` +
      `Use the fresh Simulation snapshot. If the original request asks to interpret, analyze, explain, or report results, call get_simulation_results and answer from measured values. ` +
      `Do not run, save, or modify the Simulation again. Original user request: ${request.originalPrompt}`);
  }

  private async continueAfterSimulationApply(request: ActiveRequest,
      action: Extract<ReturnType<typeof parseAppActions>[number], { type: 'APPLY_SIMULATION_CONFIG' }>,
      applied: AgentWorkspaceContext | null | undefined): Promise<void> {
    const original = request.scope;
    if (!applied || applied.tabType !== TabType.SIMULATION || !applied.simulation
        || applied.simulation.modelUuid !== action.modelUuid
        || applied.simulation.revision <= action.expectedSimulationRevision
        || this.workspaces.activeId() !== original.scopeId) {
      throw new Error('Simulation Apply did not produce the expected live configuration');
    }
    const fingerprint = simulationConfigurationFingerprint(applied.simulation.configuration);
    await request.transportDone;
    if (request.finished || this.active.get(original.scopeId) !== request) return;
    const next = this.workspaces.capture(original.scopeId);
    if (!next || next.tabType !== TabType.SIMULATION || !next.simulation
        || next.tabInstanceId !== original.tabInstanceId || next.dataServiceId !== original.dataServiceId
        || next.simulation.modelUuid !== action.modelUuid || this.workspaces.activeId() !== original.scopeId
        || next.simulation.revision !== applied.simulation.revision
        || simulationConfigurationFingerprint(next.simulation.configuration) !== fingerprint) {
      throw new Error('Simulation changed after Apply; Run was not started');
    }
    request.id = crypto.randomUUID();
    request.scope = next;
    request.application = this.workspaces.applicationMetadata();
    request.sequence = 0;
    request.finalizing = false;
    request.allowSave = false;
    request.postApplyInterpret = action.afterApply === 'RUN_AND_INTERPRET';
    this.sessions.update(next.scopeId, session => ({ ...session, status: 'streaming', activities: [],
      messages: session.messages.map(message => message.id === request.messageId ? { ...message, text: '' } : message) }));
    this.dispatch(request, request.sessionId,
      `The Simulation configuration was applied successfully. This is an internal action-completion continuation, not a new user request. ` +
      `Use this fresh live Simulation snapshot and its revision. Stage exactly one RUN_SIMULATION action for the current configuration. ` +
      `Do not rebuild, reapply, or save the configuration. Do not interpret any prior result. ` +
      `After this Run completes, the frontend will continue ${request.postApplyInterpret ? 'with measured-result interpretation' : 'with a run confirmation'}.`);
  }

  private async beginSimulationWorkflow(request: ActiveRequest,
      action: Extract<AppAction, { type: 'APPLY_SIMULATION_CONFIG' | 'RUN_SIMULATION' | 'SAVE_SIMULATION_CONFIG' }>,
      _responseText: string): Promise<void> {
    const scope = this.workspaces.capture(request.scope.scopeId);
    const before = request.scope;
    if (!scope?.simulation || scope.tabType !== TabType.SIMULATION || !before.simulation
        || scope.tabInstanceId !== before.tabInstanceId || scope.dataServiceId !== before.dataServiceId
        || scope.simulation.modelUuid !== before.simulation.modelUuid
        || this.workspaces.activeId() !== before.scopeId) {
      throw new Error('Simulation workflow changed workspace after the first action');
    }
    if (action.type === 'APPLY_SIMULATION_CONFIG'
        && (scope.simulation.revision <= action.expectedSimulationRevision || scope.simulation.modelUuid !== action.modelUuid)) {
      throw new Error('Simulation workflow Apply did not update the live revision');
    }
    if (action.type === 'RUN_SIMULATION' && (!scope.simulation.hasLastResult
        || scope.simulation.resultFreshness !== 'CURRENT')) {
      throw new Error('Simulation workflow Run completed without a current result');
    }
    if (action.type === 'SAVE_SIMULATION_CONFIG'
        && (scope.simulation.revision !== before.simulation.revision
          || simulationConfigurationFingerprint(scope.simulation.configuration)
            !== simulationConfigurationFingerprint(before.simulation.configuration)
          || (before.simulation.resultFreshness === 'CURRENT' && scope.simulation.resultFreshness !== 'CURRENT'))) {
      throw new Error('Simulation workflow Save changed the live configuration or result');
    }
    const continuation = action.simulationWorkflow!;
    request.simulationWorkflow = {
      remaining: structuredClone(continuation.remaining), persistenceIntent: structuredClone(continuation.persistenceIntent),
      revision: scope.simulation.revision, fingerprint: simulationConfigurationFingerprint(scope.simulation.configuration),
      modelUuid: scope.simulation.modelUuid, tabInstanceId: scope.tabInstanceId!, dataServiceId: scope.dataServiceId!,
      completed: [action.type === 'APPLY_SIMULATION_CONFIG' ? 'applied' : action.type === 'RUN_SIMULATION' ? 'run' : 'saved'],
      ran: action.type === 'RUN_SIMULATION'
    };
    request.scope = scope;
    await this.advanceSimulationWorkflow(request);
  }

  /** Execute only the finite typed Simulation steps authorized by this request.
   * Every mutating step is checked against a newly captured live snapshot. */
  private async advanceSimulationWorkflow(request: ActiveRequest): Promise<void> {
    await request.transportDone;
    if (request.finished || this.active.get(request.scope.scopeId) !== request) return;
    const workflow = request.simulationWorkflow;
    if (!workflow) throw new Error('Simulation workflow state is missing');
    while (workflow.remaining.length) {
      if (request.finished || this.active.get(request.scope.scopeId) !== request) return;
      const scope = this.workspaces.capture(request.scope.scopeId);
      if (!scope?.simulation || scope.tabType !== TabType.SIMULATION
          || scope.tabInstanceId !== workflow.tabInstanceId || scope.dataServiceId !== workflow.dataServiceId
          || scope.simulation.modelUuid !== workflow.modelUuid || this.workspaces.activeId() !== scope.scopeId
          || scope.simulation.revision !== workflow.revision
          || simulationConfigurationFingerprint(scope.simulation.configuration) !== workflow.fingerprint) {
        throw new Error('Simulation workflow stopped: the live configuration changed');
      }
      request.scope = scope;
      const step = workflow.remaining.shift()!;
      if (step.kind === 'INTERPRET') {
        if (workflow.ran && (!scope.simulation.hasLastResult || scope.simulation.resultFreshness !== 'CURRENT')) {
          throw new Error('Simulation workflow stopped: the requested Run result is not current');
        }
        workflow.awaitingInterpret = true;
        request.id = crypto.randomUUID();
        request.application = this.workspaces.applicationMetadata();
        request.sequence = 0;
        request.finalizing = false;
        request.allowSave = false;
        this.sessions.update(scope.scopeId, session => ({ ...session, status: 'streaming', activities: [],
          messages: session.messages.map(message => message.id === request.messageId ? { ...message, text: '' } : message) }));
        this.dispatch(request, request.sessionId,
          `The requested Simulation workflow has reached result interpretation. This is an internal continuation, not a new user request. ` +
          `Call get_simulation_results and interpret the current measured result only. Do not save, run, apply, or modify anything.`);
        return;
      }
      let nextAction: AppAction;
      if (step.kind === 'RUN') {
        nextAction = { actionId: crypto.randomUUID(), type: 'RUN_SIMULATION',
          expectedSimulationRevision: scope.simulation.revision };
      } else {
        if (workflow.persistenceIntent.mode !== step.mode
            || (step.mode === 'SAVE_AS' && (workflow.persistenceIntent.mode !== 'SAVE_AS'
              || workflow.persistenceIntent.name !== step.name))) {
          throw new Error('Simulation workflow stopped: Save was not authorized for this request');
        }
        nextAction = { actionId: crypto.randomUUID(), type: 'SAVE_SIMULATION_CONFIG', mode: step.mode,
          ...(step.mode === 'SAVE_AS' ? { simulationName: step.name } : {}),
          persistenceIntent: workflow.persistenceIntent };
      }
      try {
        await this.appActions.executeBatch([nextAction], scope, this.workspaces.applicationMetadata().app_revision);
      } catch (error) {
        throw new Error(`Simulation workflow ${step.kind === 'RUN' ? 'Run' : 'Save'} failed: ${error instanceof Error ? error.message : 'unknown error'}`);
      }
      if (request.finished || this.active.get(scope.scopeId) !== request) return;
      const after = this.workspaces.capture(scope.scopeId);
      if (!after?.simulation || after.tabInstanceId !== workflow.tabInstanceId
          || after.dataServiceId !== workflow.dataServiceId || after.simulation.modelUuid !== workflow.modelUuid
          || this.workspaces.activeId() !== scope.scopeId) {
        throw new Error('Simulation workflow stopped: workspace changed after the action');
      }
      if (step.kind === 'SAVE') {
        if (after.simulation.revision !== workflow.revision
            || simulationConfigurationFingerprint(after.simulation.configuration) !== workflow.fingerprint
            || (workflow.ran && after.simulation.resultFreshness !== 'CURRENT')) {
          throw new Error('Simulation workflow stopped: Save changed the live configuration or result');
        }
        workflow.completed.push(step.mode === 'SAVE_AS' ? `saved as ${step.name}` : 'saved');
      } else {
        if (after.simulation.revision !== workflow.revision || !after.simulation.hasLastResult
            || after.simulation.resultFreshness !== 'CURRENT') {
          throw new Error('Simulation workflow Run completed without a current result');
        }
        workflow.ran = true;
        workflow.completed.push('run');
      }
      workflow.revision = after.simulation.revision;
      workflow.fingerprint = simulationConfigurationFingerprint(after.simulation.configuration);
      request.scope = after;
    }
    const finalText = workflow.interpreted || `Simulation ${workflow.completed.join(', ')}. No additional actions were requested.`;
    request.simulationWorkflow = undefined;
    this.completeBenchmark(request, 'completed');
    request.finished = true;
    this.recovery.delete(request.scope.scopeId);
    this.active.delete(request.scope.scopeId);
    this.sessions.update(request.scope.scopeId, session => ({ ...session, status: 'idle', error: undefined,
      choices: this.resolveChoice(session.choices, request),
      messages: session.messages.map(message => message.id === request.messageId ? { ...message, text: finalText } : message) }));
  }

  private mergeJournals(prior: AgentMutationJournal | undefined, current: AgentMutationJournal): AgentMutationJournal {
    if (!prior) return structuredClone(current);
    return Object.fromEntries(Object.keys(current).map(key => {
      const items = [...(prior[key as keyof AgentMutationJournal] ?? []), ...current[key as keyof AgentMutationJournal]];
      return [key, [...new Map(items.map(item => [JSON.stringify(item), item])).values()]];
    })) as unknown as AgentMutationJournal;
  }

  private fail(request: ActiveRequest, error: string): void {
    if (request.finished) return;
    if (request.prior) this.recovery.set(request.scope.scopeId, {
      scope: request.scope, journal: request.prior, originalId: request.live?.context.rebase?.originalRequestId ?? request.id
    });
    this.completeBenchmark(request, 'failed');
    request.finished = true;
    request.simulationWorkflow = undefined;
    request.transportComplete?.();
    if (request.live) this.liveAdapter.release(request.live);
    this.active.delete(request.scope.scopeId);
    request.subscription.unsubscribe();
    this.sessions.update(request.scope.scopeId, session => ({ ...session, status: 'error', error,
      attachments: session.attachments.length ? session.attachments : request.attachments,
      choices: request.choice ? session.choices.map(item => item.choiceId === request.choice?.choiceId
        ? { ...item, status: 'pending' } : item) : session.choices }));
  }

  private recordDiagnostic(scopeId: string, requestId: string, stage: string, status: string,
                           details: Record<string, unknown>): void {
    this.sessions.update(scopeId, session => ({ ...session, diagnostics: [...session.diagnostics,
      { requestId, stage, status, details }].slice(-AgentClientService.maxDiagnosticEvents) }));
  }

  private startFrontendOperation(request: ActiveRequest, operation: string,
      details: Record<string, unknown> = {}): number {
    const startedAt = performance.now();
    this.recordDiagnostic(request.scope.scopeId, request.id, 'frontend', 'started', { operation, ...details });
    return startedAt;
  }

  private finishFrontendOperation(request: ActiveRequest, operation: string, startedAt: number,
      status: 'completed' | 'failed' = 'completed', details: Record<string, unknown> = {}): void {
    this.recordDiagnostic(request.scope.scopeId, request.id, 'frontend', status, {
      operation, duration_ms: Math.max(0, Math.round(performance.now() - startedAt)), ...details
    });
  }

  private completeBenchmark(request: ActiveRequest, outcome: 'completed' | 'failed' | 'cancelled'): void {
    this.recordDiagnostic(request.scope.scopeId, request.id, 'benchmark', 'completed', {
      operation: 'agent_workflow', outcome, request_count: request.dispatchCount,
      duration_ms: Math.max(0, Math.round(performance.now() - request.benchmarkStartedAtMs))
    });
  }

  /** Only stable, non-sensitive client-side failure categories belong in the trace. */
  private createdModelFailureReason(error: unknown): string {
    if (!(error instanceof Error)) return 'unknown';
    const known = new Set([
      'Invalid created Model', 'Creator is no longer empty', 'Invalid Model read response',
      'Creator contains an existing Canvas',
      'Creator changed before loading', 'The created Model returned no Canvas elements',
      'Created Model Canvas unavailable', 'Live Creator Canvas unavailable',
      'Canvas changed while preparing the request'
    ]);
    return known.has(error.message) ? error.message.toLowerCase().replaceAll(' ', '_') : 'unexpected_client_error';
  }

  /** Preserve a bounded backend reason when an application action rejects
   * with an HttpErrorResponse-shaped object instead of a native Error. */
  private operationFailureMessage(error: unknown): string {
    const seen = new Set<unknown>();
    const extract = (value: unknown, depth: number): string | undefined => {
      if (depth > 5 || value == null || seen.has(value)) return undefined;
      if (typeof value === 'string') return value.trim() || undefined;
      if (typeof value !== 'object') return undefined;
      seen.add(value);
      const record = value as Record<string, unknown>;
      // HttpErrorResponse.message is generic; inspect its response body first.
      for (const key of ['error', 'detail', 'msg', 'reason', 'message']) {
        const candidate = extract(record[key], depth + 1);
        if (candidate) return candidate;
      }
      if (Array.isArray(value)) {
        for (const item of value) {
          const candidate = extract(item, depth + 1);
          if (candidate) return candidate;
        }
      }
      return value instanceof Error && value.message ? value.message : undefined;
    };
    const message = extract(error, 0) ?? 'unknown error';
    return message.replace(/\s+/g, ' ').slice(0, 600);
  }

  private resolveChoice(choices: readonly AgentChoice[], request: ActiveRequest): readonly AgentChoice[] {
    return request.choice ? choices.map(item => item.choiceId === request.choice?.choiceId
      ? { ...item, status: 'resolved' } : item) : choices;
  }

  /** A restored VIEW is a persisted checkpoint; every MODEL_CREATOR owns a live Canvas. */
  private usesLiveCreator(scope: AgentWorkspaceContext): boolean {
    return scope.tabType === TabType.MODEL_CREATOR;
  }

  private requiresLiveCanvas(scope: AgentWorkspaceContext, prompt: string): boolean {
    // A MODEL_CREATOR owns one authoritative live Canvas on every turn. Do
    // not classify natural-language verbs here: wording, accents, typos and
    // read-only Canvas questions must never decide whether the runtime sees
    // the current Canvas. Operation tools still enforce target resolution,
    // revisions, and the no-persist rule independently.
    void prompt;
    // A blank Creator has no Canvas yet; Model creation uses its dedicated
    // created-model continuation before any live Canvas exists.
    return this.usesLiveCreator(scope) && scope.modelTargetState !== 'empty';
  }

  private async readAttachment(file: File): Promise<AgentAttachment> {
    const mimeType = attachmentMimeType(file);
    if (!mimeType) throw new Error('Supported files are TXT, Markdown, CSV, JSON, PDF, PNG, JPG, and WebP.');
    if (!file.size) throw new Error(`${file.name} is empty.`);
    if (file.size > AgentClientService.maxAttachmentBytes) throw new Error(`${file.name} exceeds the 10 MB limit.`);
    const bytes = new Uint8Array(await file.arrayBuffer());
    return { name: file.name.slice(0, 180), mimeType, sizeBytes: bytes.byteLength, dataBase64: bytesToBase64(bytes) };
  }
}

function attachmentMimeType(file: File): AgentAttachment['mimeType'] | undefined {
  const extension = file.name.split('.').pop()?.toLowerCase();
  const fromExtension: Record<string, AgentAttachment['mimeType']> = {
    txt: 'text/plain', md: 'text/markdown', csv: 'text/csv', json: 'application/json', pdf: 'application/pdf',
    png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', webp: 'image/webp'
  };
  return extension ? fromExtension[extension] : undefined;
}

function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  for (let start = 0; start < bytes.length; start += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(start, start + 0x8000));
  }
  return btoa(binary);
}

function actionsIncludeViewRestore(raw: unknown): boolean {
  return Array.isArray(raw) && raw.some(action => isRecord(action) && action['type'] === 'RESTORE_VIEW');
}
